import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import JSZip from "jszip";
import { CanvasContext } from "../../../contexts/CanvasContext";
import { useGenerateHTML } from "./HTMLGenerator";
import { useGenerateCSS } from "./CSSGenerator";
import { useGenerateJS } from "./JSGenerator";
import indexedDBService from "../../../services/IndexedDBService";
import Prism from "prismjs";
import "prismjs/themes/prism.css";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-css";
import "prismjs/components/prism-javascript";

const CodeGenerator = ({
  showCode,
  showPreview,
  triggerDownload,
  setTriggerDownload,
}) => {
  const { generateHTML } = useGenerateHTML();
  const { generateCSS } = useGenerateCSS();
  const { generateJS } = useGenerateJS();
  const {
    flatList,
    currentPage,
    setCurrentPage,
    pages,
    files,
    generateFlatList,
    updateDownloadProgress,
    setIsDownloading,
  } = useContext(CanvasContext);
  const [generatedHTML, setGeneratedHTML] = useState("");
  const [generatedCSS, setGeneratedCSS] = useState("");
  const [generatedJS, setGeneratedJS] = useState("");
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // const loadingOverlayStyle = {
  //   position: "fixed",
  //   top: 0,
  //   left: 0,
  //   width: "100%",
  //   height: "100%",
  //   backgroundColor: "rgba(255, 255, 255, 0.8)",
  //   display: "flex",
  //   alignItems: "center",
  //   justifyContent: "center",
  //   zIndex: 1000,
  // };

  // const loadingSpinnerStyle = {
  //   border: "8px solid #f3f3f3",
  //   borderTop: "8px solid #3498db",
  //   borderRadius: "50%",
  //   width: "50px",
  //   height: "50px",
  //   animation: "spin 2s linear infinite",
  // };

  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);

  const generateAllCode = useCallback(
    (elements, pageName) => {
      const html = generateHTML(elements, pageName);
      const css = generateCSS(elements, files);
      const js = generateJS(elements);
      return { html, css, js };
    },
    [generateHTML, generateCSS, generateJS, files]
  );

  const generateCode = useCallback(() => {
    const { html, css, js } = generateAllCode(flatList, currentPage);
    setGeneratedHTML(html);
    setGeneratedCSS(css);
    setGeneratedJS(js);
  }, [flatList, currentPage, generateAllCode]);

  useEffect(() => {
    generateCode();
  }, [generateCode]);

  const fetchPageData = useCallback(
    async (pageId) => {
      const canvasDivisions = await indexedDBService.getCanvasDivisions(pageId);
      const flatList = generateFlatList(canvasDivisions);
      const files = await indexedDBService.getFiles(pageId);
      return { flatList, files };
    },
    [generateFlatList]
  );

  const downloadAllPages = useCallback(async () => {
    setIsDownloading(true);
    const zip = new JSZip();
    const totalPages = pages.length - 1;
    let completedPages = 0;
    const startTime = Date.now();

    for (const page of pages.slice(1)) {
      await setCurrentPage(page);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const pageData = await fetchPageData(page.id);
      const { html, css, js } = generateAllCode(pageData.flatList, page);
      const safeName = page.name.replace(/\s+/g, "_");
      zip.file(`${safeName}.html`, html);
      zip.file(`${safeName}.css`, css);
      zip.file(`${safeName}.js`, js);

      pageData.files.forEach((file) => {
        if (
          file.type.startsWith("image/") ||
          file.type.startsWith("video/") ||
          file.type.startsWith("audio/")
        ) {
          zip.file(file.name, file.data.split(",")[1], { base64: true });
        } else if (file.type === "application/json") {
          try {
            const jsonData = JSON.parse(file.data);
            zip.file(file.name, JSON.stringify(jsonData, null, 2));
          } catch (error) {
            console.error(`Error parsing JSON file ${file.name}:`, error);
            zip.file(file.name, file.data);
          }
        }
      });

      completedPages++;
      const elapsedTime = (Date.now() - startTime) / 1000;
      const remainingTime =
        (elapsedTime / completedPages) * (totalPages - completedPages);
      updateDownloadProgress({
        current: completedPages,
        total: totalPages,
        percentage: Math.round((completedPages / totalPages) * 100),
        remainingTime: remainingTime,
      });
    }

    const content = await zip.generateAsync({ type: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(content);
    link.download = "project.zip";
    link.click();

    updateDownloadProgress({
      current: totalPages,
      total: totalPages,
      percentage: 100,
      remainingTime: 0,
    });
    setTimeout(() => {
      setIsDownloading(false);
      updateDownloadProgress({
        current: 0,
        total: 0,
        percentage: 0,
        remainingTime: 0,
      });
    }, 3000);
  }, [
    pages,
    setCurrentPage,
    generateAllCode,
    updateDownloadProgress,
    setIsDownloading,
    fetchPageData,
  ]);

  useEffect(() => {
    if (triggerDownload) {
      downloadAllPages();
      setTriggerDownload(false);
    }
  }, [triggerDownload, setTriggerDownload, downloadAllPages]);

  const replaceImagePaths = useCallback(
    (content, fileType) => {
      return content.replace(
        /(?:src=['"]|url\()['"]?(\.\/[^'")]+)['"]?\)?/g,
        (match, path) => {
          const fileName = path.split("/").pop();
          const file = files.find((f) => f.name === fileName);
          if (file) {
            if (fileType === "html") {
              return `src="${file.data}"`;
            } else if (fileType === "css") {
              return `url("${file.data}")`;
            }
          }
          return match;
        }
      );
    },
    [files]
  );

  useEffect(() => {
    if (iframeRef.current && showPreview) {
      const iframe = iframeRef.current;
      const iframeDocument =
        iframe.contentDocument || iframe.contentWindow.document;
      iframeDocument.open();
      const htmlWithReplacedPaths = replaceImagePaths(generatedHTML, "html");
      const cssWithReplacedPaths = replaceImagePaths(generatedCSS, "css");

      // Improved skeleton structure
      const skeletonHTML = `
        <header class="skeleton-header">
          <nav class="skeleton-nav">
            <div class="skeleton-logo"></div>
            <ul class="skeleton-menu">
              <li></li>
              <li></li>
              <li></li>
            </ul>
          </nav>
        </header>
        <main class="skeleton-main">
          <section class="skeleton-hero">
            <h1 class="skeleton-title"></h1>
            <p class="skeleton-text"></p>
            <button class="skeleton-button"></button>
          </section>
          <section class="skeleton-content">
            <div class="skeleton-column"></div>
            <div class="skeleton-column"></div>
            <div class="skeleton-column"></div>
          </section>
        </main>
        <footer class="skeleton-footer">
          <div class="skeleton-footer-content"></div>
        </footer>
      `;

      const skeletonCSS = `
        .skeleton-header, .skeleton-main, .skeleton-footer {
          padding: 20px;
          background-color: #f0f0f0;
        }
        .skeleton-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .skeleton-logo {
          width: 100px;
          height: 40px;
          background-color: #ddd;
        }
        .skeleton-menu {
          display: flex;
          list-style-type: none;
          padding: 0;
        }
        .skeleton-menu li {
          width: 60px;
          height: 20px;
          background-color: #ddd;
          margin-left: 20px;
        }
        .skeleton-hero {
          text-align: center;
          padding: 50px 0;
        }
        .skeleton-title {
          width: 60%;
          height: 40px;
          background-color: #ddd;
          margin: 0 auto 20px;
        }
        .skeleton-text {
          width: 80%;
          height: 60px;
          background-color: #ddd;
          margin: 0 auto 20px;
        }
        .skeleton-button {
          width: 120px;
          height: 40px;
          background-color: #ddd;
          border: none;
        }
        .skeleton-content {
          display: flex;
          justify-content: space-between;
          margin-top: 50px;
        }
        .skeleton-column {
          width: 30%;
          height: 200px;
          background-color: #ddd;
        }
        .skeleton-footer {
          margin-top: 50px;
        }
        .skeleton-footer-content {
          width: 100%;
          height: 60px;
          background-color: #ddd;
        }
      `;

      iframeDocument.write(`
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <style>
            body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
            * { box-sizing: border-box; }
            ${skeletonCSS}
            ${cssWithReplacedPaths}
          </style>
        </head>
        <body>
          <div id="skeleton" style="display: block;">
            ${skeletonHTML}
          </div>
          <div id="content" style="display: none;">
            ${htmlWithReplacedPaths}
          </div>
          <script>
            ${generatedJS}
            window.addEventListener('load', function() {
              document.getElementById('skeleton').style.display = 'none';
              document.getElementById('content').style.display = 'block';
            });
          </script>
        </body>
        </html>
      `);
      iframeDocument.close();

      const handleLoad = () => setLoading(false);
      iframe.addEventListener("load", handleLoad);
      return () => {
        if (iframe) {
          iframe.removeEventListener("load", handleLoad);
        }
      };
    }
  }, [
    generatedHTML,
    generatedCSS,
    generatedJS,
    showPreview,
    replaceImagePaths,
  ]);

  useEffect(() => {
    if (showCode) {
      Prism.highlightAll();
    }
  }, [showCode, generatedHTML, generatedCSS, generatedJS]);

  return (
    <>
      {showPreview && (
        <>
          {/* {loading && (
            <div style={loadingOverlayStyle}>
              <div style={loadingSpinnerStyle}></div>
              <p>Loading...</p>
            </div>
          )} */}
          <iframe
            ref={iframeRef}
            title="preview"
            style={{
              width: "100%",
              height: "100vh",
              margin: "0",
              display: loading ? "none" : "block",
            }}
          />
        </>
      )}
      {showCode && (
        <div
          style={{
            width: "100%",
            height: "100vh",
            overflow: "auto",
            padding: "3rem",
            scrollbarWidth: "none",
            backgroundColor: "#f5f5f5",
            fontFamily: "'Fira Code', monospace",
          }}
        >
          <h3 style={{ margin: "8px", color: "#333" }}>Generated HTML:</h3>
          <pre
            style={{
              padding: "20px",
              backgroundColor: "#fff",
              borderRadius: "4px",
            }}
          >
            <code className="language-markup">{generatedHTML}</code>
          </pre>
          <h3 style={{ margin: "8px", color: "#333" }}>Generated CSS:</h3>
          <pre
            style={{
              padding: "20px",
              backgroundColor: "#fff",
              borderRadius: "4px",
            }}
          >
            <code className="language-css">{generatedCSS}</code>
          </pre>
          <h3 style={{ margin: "8px", color: "#333" }}>
            Generated JavaScript:
          </h3>
          <pre
            style={{
              padding: "20px",
              backgroundColor: "#fff",
              borderRadius: "4px",
            }}
          >
            <code className="language-javascript">{generatedJS}</code>
          </pre>
        </div>
      )}
    </>
  );
};

export default CodeGenerator;
